// Shared column definition + row builder for the "Admin Order Export" report
// (Trello card #11, comment #57). Used by BOTH the Admin Dashboard
// (admin-dashboard.component.jsx) and the Reports Tab (reports/index.js) so the
// two paths emit the IDENTICAL 21-column output.
//
// IMPORTANT: this is intentionally isolated from `getCols`
// (schedule.component.jsx). `getCols` powers the live Shipping/Schedule grid
// and the customer-facing user-reports export, which must NOT change. Do not
// route those through this module.
import { get } from 'lodash';
import moment from 'moment';
import { getRawLicense, getLicAddress, jparse } from '../../actions/utils';
import { getStatusIcon } from '../../models/order';

// Order Type value -> human readable label. Mirrors the option titles defined
// in dashboard/order/order.component.jsx (the Order Type / Reconciliation
// Return Type selects). Kept local so this report owns its own labels.
export const ORDER_TYPE_LABELS = {
  layover: 'Pickup with Layover',
  dropoff: 'Dropoff with Layover',
  sameDay: 'Same Day',
  cash_return: 'Reconciliation Return',
  rej_return: 'Rejection Return',
  cash_pickup: 'Payment Pickup',
  other_return: 'Other',
};

export const orderTypeLabel = (row) => {
  const t = get(row, 'data.order.orderType');
  if (!t) return '';
  return get(ORDER_TYPE_LABELS, t, t);
};

// The license # that identifies the destination account for this order. Pickup
// orders resolve the pickup license instead (mirrors getCols report columns).
const destLN = (row) => {
  const isPickup = get(row, 'isPickup');
  return get(row, `data.order.${isPickup ? 'pickupLN' : 'destinationLN'}`);
};

// `deleted` field -> human-readable status, for the "Deleted" column that the
// Canceled/Deleted report carries (Danielle 2026-06-03 — re-added after comment
// #57 stripped it from the shared layout). Mirrors DEL_STATUS in reports/index.js.
export const DEL_STATUS = {
  0: 'Not deleted',
  1: 'Deleted',
  2: 'Canceled',
};

// 21-column "Admin Order Export" definition (comment #57, in order).
// `reconMap` (optional) maps an order's `orderId` -> the linked reconciliation
// return order's Order ID, so the "Reconciliation Order ID" column can be
// populated without storing that id on the delivery order itself.
// When `includeDeleted` is true (the "Canceled/Deleted Orders" report only) a
// "Deleted" column is inserted before "Delivery Status" — its original position
// before comment #57 removed it from the main report.
export const getAdminOrderExportCols = (reconMap = {}, { includeDeleted = false } = {}) => {
  const cols = [
  { title: 'Order ID', value: row => get(row, 'orderId', '') || '' },
  { title: 'Vendor', value: row => get(row, 'data.pickupCompany.Licensee', '') || '' },
  { title: 'Destination License #', value: row => destLN(row) || '' },
  { title: 'Destination Name', value: row => get(getRawLicense(destLN(row), {}), 'Licensee', '') || '' },
  { title: 'Destination DBA', value: row => get(getRawLicense(destLN(row), {}), 'DBA', '') || '' },
  { title: 'Destination Address', value: row => getLicAddress(destLN(row), ['Street Address', 'City']) || '' },
  { title: 'Destination City', value: row => get(getRawLicense(destLN(row), {}), 'City', '') || '' },
  { title: 'Destination Zip', value: row => get(getRawLicense(destLN(row), {}), 'Zip', '') || '' },
  { title: 'Customer Invoice', value: row => get(row, 'data.billing.invoice', '') || '' },
  { title: 'Invoice Amount', value: row => get(row, 'data.billing.amount', '') || '' },
  { title: 'Payment Method', value: row => get(row, 'data.billing.method', '') || '' },
  { title: 'Manifest Number', value: row => get(row, 'data.billing.manifest', '') || '' },
  { title: 'Delivery Date', value: row => {
    const d = get(row, 'data.order.deliveryDate');
    return d ? moment(d).format('L') : '';
  } },
  { title: 'Delivery Route', value: row => get(jparse(get(row, 'data.order.route')), 'name', '') || '' },
  { title: 'Delivery Status', value: row => get(getStatusIcon(row), 'title', '') || '' },
  { title: 'Box Count', value: row => get(row, 'data.order.weight', '') || '' },
  { title: 'Order Type', value: row => orderTypeLabel(row) },
  { title: 'Delivery Invoice', value: row => get(row, 'data.billing.gpsinvoice', '') || '' },
  { title: 'Reconciliation Order ID', value: row => get(reconMap, get(row, 'orderId'), '') || '' },
  { title: 'Billing Notes', value: row => get(row, 'data.order.billingNotes', '') || '' },
  { title: 'Delivery Notes', value: row => get(row, 'data.order.notes', '') || '' },
  ];
  if (includeDeleted) {
    const at = cols.findIndex(c => c.title === 'Delivery Status');
    cols.splice(at < 0 ? cols.length : at, 0, {
      title: 'Deleted',
      value: row => get(DEL_STATUS, get(row, 'deleted', 0), '') || '',
    });
  }
  return cols;
};

// `deleted` field semantics: 0 = Not deleted, 1 = Deleted, 2 = Canceled.
export const isDeletedOrCanceled = (order) => get(order, 'deleted', 0) > 0;

// Build CSV rows (header + data) for the export. `deletedReport=false` keeps
// only live orders (the main "Admin Order Export"); `deletedReport=true` keeps
// only Deleted/Canceled orders (the "Canceled/Deleted Orders" report).
export const buildAdminExportRows = (orders = [], { deletedReport = false, reconMap = {} } = {}) => {
  const cols = getAdminOrderExportCols(reconMap, { includeDeleted: deletedReport });
  const filtered = (orders || []).filter(o =>
    deletedReport ? isDeletedOrCanceled(o) : !isDeletedOrCanceled(o)
  );
  const rows = [cols.map(c => c.title)];
  filtered.forEach(order => {
    rows.push(cols.map(c => c.value(order)));
  });
  return rows;
};

// Build a map of member-order `orderId` -> linked reconciliation return order's
// Order ID, from the recon list returned by `getReconList`. Each recon row
// carries `returnOrderId` (the cash_return order's Order ID) and the underlying
// member orders' ids in `searchData.orderIds` / `data.orderdata`.
export const buildReconMap = (recons = []) => {
  const map = {};
  (recons || []).forEach(recon => {
    const returnOrderId = get(recon, 'returnOrderId');
    if (!returnOrderId) return;
    const memberIds = get(recon, 'searchData.orderIds', []);
    memberIds.forEach(oid => { if (oid) map[oid] = returnOrderId; });
    // Fall back to orderdata[].orderId in case searchData is unavailable.
    get(recon, 'data.orderdata', []).forEach(od => {
      const oid = get(od, 'orderId');
      if (oid && !map[oid]) map[oid] = returnOrderId;
    });
  });
  return map;
};
